<template>
  <div>
    <Head :title="$t('models.candidate')"/>

    <div class="flex justify-start mb-8">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" :href="route('admin.elections.index')">
          {{ $t('models.election') }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        <Link class="text-indigo-400 hover:text-indigo-600"
              :href="route('admin.elections.edit', election.uuid)">
          {{ election.name }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        {{ $t('actions.update_model_detail', [$t('models.candidate')]) }}
      </h1>

      <div class="ml-auto">
        <Link class="inline-block btn-indigo"
              :href="route('admin.elections.candidates.index', election.uuid)">
          <span>{{ $t('ui.back') }}</span>
        </Link>
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-hidden mb-8">
      <div class="flex flex-wrap -mb-8 -mr-6 p-8">
        <text-input v-model="election.name" class="pb-8 pr-6 w-full lg:w-1/3"
                    :label="$t('fields.election.name')" disabled/>
        <text-input v-model="election.datetime_start" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_start')" disabled/>
        <text-input v-model="election.datetime_end" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_end')" disabled/>
        <text-input v-model="election.candidates_count" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.candidates_count')" disabled/>
        <text-input v-model="election.ballots_count" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.ballots_count')" disabled/>
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <form @submit.prevent="update">
        <table class="w-full whitespace-nowrap">
          <thead>
          <tr class="text-left font-bold">
            <th class="w-px"></th>
            <th class="w-px"></th>
            <th class="pb-4 pt-6 px-4">{{ $t('fields.member.full_name_cn') }}</th>
            <th class="pb-4 pt-6 px-4">{{ $t('fields.member.membership_number') }}</th>
            <th class="pb-4 pt-6 px-4">{{ $t('fields.member.state') }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="member in members" :key="member.uuid" @click="toggleCheckbox(member.uuid)"
              class="hover:bg-gray-100 focus-within:bg-gray-100 cursor-pointer">
            <td class="pl-4 pr-2 py-4 border-t">
              <input type="checkbox" :ref="member.uuid" :id="member.uuid" :value="member.uuid"
                     v-model="form.member_uuids">
            </td>
            <td class="px-2 py-4 border-t">
              <img v-if="member.photo_url" class="block -my-2 mx-2 w-8 h-8 rounded"
                   :src="member.photo_url" alt="member photo"/>
            </td>
            <td class="px-4 py-4 border-t">
              {{ member.display_name }}
            </td>
            <td class="px-4 py-4 border-t">
              {{ member.membership_number }}
            </td>
            <td class="px-4 py-4 border-t">
              {{ member.state }}
            </td>
          </tr>
          <tr v-if="members.length === 0">
            <td class="px-4 py-4 border-t" colspan="4">{{ $t('alerts.no_record_found') }}</td>
          </tr>
          </tbody>
        </table>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button class="btn-indigo" tabindex="-1" type="button" @click="checkAll">
            {{ $t('labels.select_all') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('actions.save') }}
          </loading-button>
        </div>
      </form>
    </div>

  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Layouts/Authenticated'
import TextInput from '@/Inputs/TextInput'
import FileInput from '@/Inputs/FileInput'
import SelectInput from '@/Inputs/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import TextareaInput from "@/Inputs/TextareaInput";

export default {
  components: {
    TextareaInput,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    election: Object,
    members: Array,
    selectedMemberUuids: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        member_uuids: this.selectedMemberUuids,
      }),
    }
  },
  methods: {
    checkAll() {
      this.members.forEach((member) => {
        const checkboxInput = this.$refs[member.uuid][0];
        if (checkboxInput) {
          checkboxInput.checked = true;
          this.addCandidate(member.uuid);
        }
      });
    },
    isChecked(memberUuid) {
      if (this.form.member_uuids.find(id => id === memberUuid)) {
        const checkboxInput = this.$refs[memberUuid][0];
        checkboxInput.checked = true;
      }
    },
    toggleCheckbox(memberUuid) {
      const checkboxInput = this.$refs[memberUuid][0];
      if (checkboxInput) {
        checkboxInput.checked = !checkboxInput.checked;
        if (checkboxInput.checked) {
          this.addCandidate(memberUuid);
        } else {
          this.removeCandidate(memberUuid);
        }
      }
    },
    addCandidate(memberUuid) {
      if (this.form.member_uuids.find(id => id === memberUuid)) return;
      this.form.member_uuids.push(memberUuid);
    },
    removeCandidate(memberUuid) {
      const item = this.form.member_uuids.find(id => id === memberUuid)
      const index = this.form.member_uuids.indexOf(item);
      if (index !== -1) {
        this.form.member_uuids.splice(index, 1);
      }
    },
    update() {
      this.form.post(route('admin.elections.candidates.update', this.election.uuid))
    },
  },
}
</script>
